import { useState } from "react";

const AY_OPTIONS = ["AY2026-27", "AY2025-26"];

export default function CapitalGain() {
  const [files, setFiles] = useState<File[]>([]);
  const [ay, setAy] = useState("AY2026-27");
  const [clientName, setClientName] = useState("");
  const [fy, setFy] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files as FileList)]);
    }
  };

  const removeFile = (idx: number) => {
    setFiles(prev => prev.filter((_: File, i: number) => i !== idx));
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      setError("Please select at least one file"); return;
    }
    setLoading(true); setError(""); setSuccess(false);
    const formData = new FormData();
    files.forEach((f: File) => formData.append("files", f));
    formData.append("assessment_year", ay);
    formData.append("client_name", clientName);
    formData.append("financial_year", fy);
    try {
      const response = await fetch("/api/capital-gain/calculate", {
        method: "POST",
        body: formData,
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || "Processing failed");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "TaxThinker_CapitalGain_" + (clientName || "Report") + "_" + ay + ".xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <span className="text-5xl">📈</span>
        <h1 className="text-3xl font-bold text-gray-800 mt-4">
          Capital Gain Calculator
        </h1>
        <p className="text-gray-500 mt-3 max-w-2xl mx-auto">
          Upload your broker report (PDF, Excel or CSV) from Zerodha, Groww,
          AngelOne, HDFC Securities or any mutual fund platform.
          Get a 5-sheet Excel report with transactions, tax calculation,
          financials and ITR filing guidance.
        </p>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border p-8 space-y-6">

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Assessment Year
            </label>
            <select
              value={ay}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setAy(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {AY_OPTIONS.map((o: string) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Client Name (optional)
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientName(e.target.value)}
              placeholder="e.g. Ramesh Kumar"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Financial Year (optional)
            </label>
            <input
              type="text"
              value={fy}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFy(e.target.value)}
              placeholder="e.g. 2025-26"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Upload Files (PDF / Excel / CSV — multiple files allowed)
          </label>
          <div
            className="border-2 border-dashed border-blue-200 rounded-xl p-8 text-center cursor-pointer hover:border-blue-400 transition"
            onClick={() => document.getElementById("cgFileInput")!.click()}>
            <input
              id="cgFileInput"
              type="file"
              multiple
              accept=".pdf,.xlsx,.xls,.csv"
              className="hidden"
              onChange={handleFiles}
            />
            <div className="text-4xl mb-2">☁️</div>
            <p className="text-gray-600 font-medium">Click to upload broker reports</p>
            <p className="text-sm text-gray-400 mt-1">PDF, Excel or CSV — upload multiple files at once</p>
          </div>
        </div>

        {files.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-700">Files selected ({files.length}):</p>
            {files.map((f: File, idx: number) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2 border">
                <div className="flex items-center gap-2">
                  <span className="text-lg">{f.name.endsWith(".pdf") ? "📄" : "📊"}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-700">{f.name}</p>
                    <p className="text-xs text-gray-400">{(f.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button onClick={() => removeFile(idx)} className="text-red-400 hover:text-red-600 text-sm">Remove</button>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm">
            Your Capital Gain report has been downloaded successfully!
          </div>
        )}

        <button
          onClick={handleUpload}
          disabled={files.length === 0 || loading}
          className="w-full bg-blue-700 text-white py-3 rounded-xl font-semibold hover:bg-blue-800 transition disabled:opacity-50 disabled:cursor-not-allowed text-lg">
          {loading ? "Processing... Please wait (30-90 seconds)" : "Generate Capital Gain Report"}
        </button>

        <div className="bg-blue-50 rounded-xl p-5">
          <p className="text-sm font-semibold text-blue-800 mb-3">Your report will contain 5 sheets:</p>
          <div className="grid md:grid-cols-2 gap-2 text-sm text-blue-700">
            <div>Sheet 1 — All transactions in detail</div>
            <div>Sheet 2 — Capital Gain Summary (scheme-wise)</div>
            <div>Sheet 3 — Tax Calculation (STCG + LTCG)</div>
            <div>Sheet 4 — Financials (F&O / Intraday P&L)</div>
            <div className="md:col-span-2">Sheet 5 — How to report in ITR Utility, Computax and Winman</div>
          </div>
        </div>

        {ay === "AY2025-26" && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm text-yellow-800">
            <strong>AY 2025-26 selected:</strong> Tax rates will be split — old rates (STCG 15% / LTCG 10% / exemption Rs 1 lakh) for transactions before 23 July 2024, and new rates (STCG 20% / LTCG 12.5% / exemption Rs 1.25 lakh) for transactions on or after 23 July 2024.
          </div>
        )}
      </div>
    </div>
  );
}
